/**
 * Last-active-context sync — CCM Handoff #1, Surface A (the lexide consumer).
 *
 * Mounts once at the app shell (next to `SelectionUrlSync`). Two halves:
 *
 *   - WRITE (PUT-on-switch): persists the last-active ENTITY context
 *     (client/matter/case) to the server `/api/lx/context` record so it is no
 *     longer write-never. `global` is deliberately NOT persisted — merely
 *     visiting `/home` or `/chat` (where `SelectionUrlSync` `clear()`s the
 *     selection) must not wipe the last-active CASE the resume chip relies on.
 *     The record holds the last entity context; a fresh user keeps the
 *     server's `global` default. The IM resolver (Wave 4) reads this record.
 *   - READ (active resume, miniplan H2): on a FRESH-tab neutral landing (`/` or
 *     `/home`) with no deep-linked selection, REDIRECTS to the lawyer's last
 *     context — the case workspace, else the global conversation. The
 *     destination route owns the selection from there (`CaseRouteResolver` for
 *     a case, `SelectionUrlSync`'s clear for `/chat`).
 *
 * ## The reversal of CCM Handoff #1, recorded
 *
 * This half used to be strictly passive: it seeded the case as a stale
 * selection and let the "Resume <case> →" chip offer the trip, under the
 * recorded rule *"NO auto-navigation — the lawyer decides."* That is
 * SUPERSEDED by operator direction 2026-07-23 (`D-2` of
 * `ai-docs/lexide-home-flow-redesign-miniplan.md`): `/home` predates the global
 * case and the navigator column, and a returning lawyer landing on a launcher
 * has to re-choose a context they never left. The chip survives for
 * NON-neutral landings; only the neutral ones now navigate.
 *
 * Two limits keep the reversal honest. A path carrying an id
 * (`/home/:conversationId`, any deep link) is never overridden — the lawyer's
 * expressed intent outranks their history. And the redirect is bounded by
 * `LANDING_DECISION_TIMEOUT_MS`, after which it gives up and leaves the lawyer
 * on `/chat` (where the boot redirect already put them):
 * `getContext` has no timeout of its own, and a landing that can hang behind an
 * unreachable lexserver is worse than a landing that is occasionally wrong.
 *
 * ⚠ Since H3-part-3 (welcome retired) the neutral paths no longer RENDER —
 * `/` and `/home` redirect to `/chat` on the first render pass — so the
 * neutrality test reads the BOOT path (`bootPathRef`), not the live one. Read
 * the live path here and the resume silently never fires; that is exactly the
 * regression the retirement introduced.
 *
 * Boot-ordering is load-bearing: the WRITE is GATED (`writeReady` STATE — not
 * a ref, so flipping it RE-EVALUATES the current selection and a switch that
 * raced the GET still persists) until the resume GET has read the record.
 *
 * See: ai-docs/lexiun-concierge-context-model-implementation-plan.md (Gap-2)
 *      ai-docs/lexide-home-flow-redesign-miniplan.md (H2)
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { useUIStore } from "@/stores/useUIStore";
import { useDerivedChatSessionKey } from "@/hooks/useDerivedChatSessionKey";
import { getContext, putContext } from "@/lib/lexserver-client";
import { selectionToContextBody, contextKey } from "@/lib/last-active-context";
import {
  isNeutralLandingPath,
  isUnclaimedLandingPath,
  resolveLanding,
} from "@/lib/landing-decision";

/** Debounce so a burst of route changes (e.g. nested redirects) coalesces
 *  into one PUT of the settled context. */
const PUT_DEBOUNCE_MS = 600;

/** How long to wait for the landing answer before settling the verdict at
 *  `welcome`. Since H3-part-3 no surface is HELD behind this (the boot
 *  redirect already painted `/chat`); missing the deadline now costs the
 *  resume, not a blank screen. Generous enough for a warm lexserver, short
 *  enough that a dead one costs a beat rather than the session. */
const LANDING_DECISION_TIMEOUT_MS = 1500;

/** Ceiling on the redirect-in-flight flag. Since H3-part-3 this timeout is the
 *  ONLY thing that lowers it — the flag's original owner (`HomeLayout`, which
 *  lowered it on unmount) no longer renders anywhere. Kept rather than deleted
 *  because the flag is still the honest signal for "a landing redirect is in
 *  flight", which is what any future hold surface would read. */
const LANDING_REDIRECT_MAX_MS = 5000;

export function LastActiveContextSync() {
  const navigate = useNavigate();
  /**
   * `useNavigate()` returns a NEW function identity on every location change.
   * Listing it in the resume effect's deps therefore re-ran the whole landing
   * decision on every navigation — measured: a single client-side visit to
   * `/home` fired two more `GET /lx/context` calls and redirected straight back
   * into the case. That turns every deliberate exit (the header's Sair action, the
   * breadcrumb Home, the palette's "Nova conversa") into a trap the lawyer
   * cannot escape.
   *
   * The landing decision is a BOOT question and must be asked exactly once, so
   * the effect keeps stable deps and reaches `navigate` through this ref.
   */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  /**
   * The path the browser ENTERED on, captured during RENDER — this component
   * sits above `<Routes>` (App.tsx), so its render runs before react-router's
   * `<Navigate>` layout effects can replace `/` or `/home` with `/chat`.
   *
   * Load-bearing since H3-part-3: the welcome retirement made both neutral
   * paths redirect on the first render pass, so by the time the async landing
   * verdict arrives the boot path is already gone. Reading `window.location`
   * at verdict time — which is what this used to do — therefore answered "not
   * a neutral landing" on every boot, and the D-2 resume stopped firing
   * without a single test going red.
   */
  const bootPathRef = useRef(window.location.pathname);
  const selection = useActiveContextStore((s) => s.selection);
  const sessionKey = useDerivedChatSessionKey();

  /** Gates the WRITE until the resume GET has read the record. STATE (not a
   *  ref) so flipping it RE-RUNS the WRITE effect — a selection that changed
   *  during the GET window is then re-evaluated + persisted, not dropped. */
  const [writeReady, setWriteReady] = useState(false);
  /** Last PUT'd context key — seeded with the server record so resuming (or
   *  already being on) that context doesn't re-PUT what we just read. */
  const lastKeyRef = useRef<string | null>(null);

  // RESUME — read the record and decide where a fresh boot belongs.
  // Runs per mount with a `cancelled` guard so it is StrictMode-safe: the dev
  // double-mount's first (cancelled) pass is discarded and the second completes
  // + flips `writeReady`. A once-ref guard would instead let StrictMode cancel
  // the only completing pass and leave `writeReady` stuck false. The GET is
  // idempotent, so a dev double-GET is harmless.
  useEffect(() => {
    let cancelled = false;
    const { setLanding, setLandingRedirecting } = useUIStore.getState();

    // The escape hatch. `getContext` swallows its own errors but has NO
    // timeout, so a lexserver that accepts the connection and then goes quiet
    // would leave the promise pending forever — and `/home` blank behind it.
    // This releases the render regardless; the worst case is a returning lawyer
    // seeing the welcome, which is recoverable in one click. `setLanding` is
    // first-write-wins, so a real verdict arriving in the same tick still beats
    // this one rather than racing it.
    const giveUp = window.setTimeout(
      () => setLanding("welcome"),
      LANDING_DECISION_TIMEOUT_MS,
    );

    void (async () => {
      try {
        const ctx = await getContext();
        if (cancelled) return;
        lastKeyRef.current = ctx ? contextKey(ctx) : null;

        const decision = resolveLanding({
          ctx,
          hasLocalActivity: useUIStore.getState().firstActivityAt != null,
        });
        // Record the verdict even when we do NOT act on it — the rail's Home
        // entry reads it for the rest of the session, and "this lawyer is a
        // returning one" stays true whether or not they happened to boot on a
        // neutral path.
        setLanding(decision.kind === "resume" ? "resume" : "welcome");

        // Two different questions, deliberately asked of two different paths.
        //
        // "Was a resume ever on the table?" is answered by the BOOT path — `/`
        // and `/home` are the neutral landings, and neither survives the first
        // render pass now that they redirect to `/chat`.
        //
        // "Has the lawyer claimed something since?" is answered by the CURRENT
        // path, re-read HERE rather than at mount: if they navigated somewhere
        // during the round-trip they expressed an intent and must not be yanked
        // out of it. `/chat` does not count as claiming — it is where the boot
        // redirect deposited them. Same for a deep-linked selection: D-2 is
        // explicit that deep links are never overridden.
        const enteredNeutral = isNeutralLandingPath(bootPathRef.current);
        const stillUnclaimed = isUnclaimedLandingPath(window.location.pathname);
        const noDeepLinkSelection = useActiveContextStore.getState().selection == null;
        if (!enteredNeutral || !stillUnclaimed || !noDeepLinkSelection) return;

        // NOTE: we deliberately do NOT pre-seed the selection for a case
        // resume. `CaseRouteResolver` owns that call (it runs `case.locate` and
        // seeds on success), and seeding here would survive a FAILED resolve —
        // a record pointing at a deleted case would then leave `selection`
        // holding a dead caseId, which `homeTarget` reads, so the lawyer's own
        // Home button would loop them straight back into the error panel.
        // Letting the resolver own it means a dead record degrades to `/chat`.
        if (decision.kind === "resume") {
          // Raise the in-flight flag BEFORE navigating. It existed to stop the
          // welcome painting while the destination's lazy chunk loaded; with
          // the welcome retired it currently has NO READER, and what the
          // lawyer sees during that window is the global conversation the boot
          // redirect already put them on. Stated rather than deleted because
          // the brief `/chat` frame before a case resume is a real (small)
          // regression against the held-blank `/home` this replaced — the fix,
          // if it is ever worth one, is a hold on the `/chat` surface reading
          // this flag, not a second notion of "redirecting".
          setLandingRedirecting(true);
          // Lower it unconditionally on a timer — the only lowering there is
          // now that `HomeLayout` never mounts. Far above the ~240ms lazy-chunk
          // transition, so it cannot cut a real transition short.
          window.setTimeout(
            () => setLandingRedirecting(false),
            LANDING_REDIRECT_MAX_MS,
          );
          // `replace` so Back does not land on the neutral path and bounce
          // straight back through this same redirect.
          navigateRef.current(decision.to, { replace: true });
        }
      } catch {
        // best-effort — resume never blocks the app
      } finally {
        if (!cancelled) {
          window.clearTimeout(giveUp);
          setWriteReady(true);
          // No-op if a verdict already landed; the safety net for the throw
          // path, which would otherwise leave `/home` held until the timeout.
          setLanding("welcome");
        }
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(giveUp);
    };
  }, []);

  // WRITE — after the GET, persist every real ENTITY context change (debounced
  // + deduped on scope+ids).
  useEffect(() => {
    if (!writeReady) return;
    const body = selectionToContextBody(selection, sessionKey);
    // Never persist `global`: visiting /home or /chat clears the selection,
    // and writing global there would wipe the last-active case the resume
    // chip surfaces. The record tracks the last ENTITY context only.
    if (body.scope === "global") return;
    const key = contextKey(body);
    if (key === lastKeyRef.current) return;
    const timer = setTimeout(() => {
      lastKeyRef.current = key;
      void putContext(body);
    }, PUT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [selection, sessionKey, writeReady]);

  return null;
}
